import { useEffect, useState } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { ContentFeedView, useGetMyContentFeedsQuery } from '../../api/client'
import { toastError } from '../Notification'
import ContentFeedCard from './ContentFeedCard'
import ContentFeedLoadingSkeleton from './ContentFeedLoadingSkeleton'
import { Link } from 'react-router-dom'

/**
 * Displays a list of the user's content feeds as cards, with a title.
 */
export default function ContentFeedList() {
    const { data, loading, refetch } = useGetMyContentFeedsQuery({
        fetchPolicy: 'network-only',
        onError: (error) => {
            toastError(error.message)
        }
    })

    // We want to store local state because it lets us remove the feed from the list faster
    const [feeds, setFeeds] = useState<ContentFeedView[]>([])
    useEffect(() => {
        if (data?.getMyContentFeeds) {
            setFeeds(data.getMyContentFeeds)
        }
    }, [data])
    const handleOnDelete = (id: number) => {
        setFeeds((prevFeeds) => prevFeeds.filter((feed) => feed.id !== id))
        refetch()
    }

    return (
        <section className="overflow-hidden rounded-lg border border-ink-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-4 py-2.5">
                {/* Card title, not a page heading — the masthead in HomePage owns the
                    page title and the arwen dot. font-sans + !font-semibold override the
                    global Merriweather h1-h4 rule in index.css */}
                <h2 className="font-sans !font-semibold text-[13px] text-ink-900">Your feeds</h2>
                <span className="text-xs text-ink-400">
                    Feeds are saved searches that let you dive quickly into your data
                </span>
            </div>
            {loading ? (
                <div className="flex flex-row gap-4 px-4 py-4">
                    <ContentFeedLoadingSkeleton />
                    <ContentFeedLoadingSkeleton />
                    <ContentFeedLoadingSkeleton />
                </div>
            ) : (
                <div className="flex flex-wrap gap-4 px-4 py-4">
                    {feeds.length === 0 && (
                        <div className="text-sm leading-relaxed text-ink-700">
                            <p className="my-2">You don’t have any feeds yet. To create a feed:</p>
                            <ul className="my-0 list-disc pl-5">
                                <li>
                                    <Link
                                        to={'/content-search/'}
                                        className="text-arwen-600 underline hover:text-arwen-800"
                                    >
                                        search for the content you are interested in
                                    </Link>{' '}
                                    and{' '}
                                </li>
                                <li>click the Save button to save that search as a feed.</li>
                            </ul>
                            <p className="my-0 mt-2">
                                Please contact{' '}
                                <a
                                    href="mailto:support@arwen.ai"
                                    className="text-arwen-600 underline hover:text-arwen-800"
                                >
                                    support@arwen.ai
                                </a>{' '}
                                if you have any questions.
                            </p>
                        </div>
                    )}
                    {feeds.map((feed) => (
                        <ContentFeedCard
                            key={feed.id}
                            contentFeed={feed}
                            onDeleted={handleOnDelete}
                        />
                    ))}
                    {/* Feeds are created by saving a search, so this leads to Comment
                        search rather than opening a dialog here. */}
                    {feeds.length > 0 && (
                        <Link
                            to={'/content-search/'}
                            title="Save a search from Comment search as a feed"
                            className="flex w-52 items-center justify-center gap-1.5 rounded-[10px] border border-dashed border-ink-300 px-3.5 py-3 text-[13px] font-medium text-ink-500 hover:border-arwen-300 hover:bg-arwen-50/40 hover:text-arwen-700"
                        >
                            <PlusIcon className="h-4 w-4" aria-hidden="true" />
                            New feed
                        </Link>
                    )}
                </div>
            )}
        </section>
    )
}
